import React, { useEffect, useRef, useState } from 'react';
import { deserializers } from '@jscad/io'; // JSCAD parser
import { io } from '@jscad/io';
import * as THREE from 'three';
import { 
  Camera, 
  ZoomIn, 
  ZoomOut,  
  RotateCcw,
  Eye
} from 'lucide-react';

const ModelViewer = ({ url }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);  const rendererRef = useRef(null);
  const modelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [transparency, setTransparency] = useState(0);
  const [viewMode, setViewMode] = useState('shaded');
  const lastMousePos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;

    // Setup camera
    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 5);
    cameraRef.current = camera;

    // Setup renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    // Add grid
    const gridHelper = new THREE.GridHelper(10, 10);
    scene.add(gridHelper);

    // Mouse event handlers for basic rotation
    const handleMouseDown = (e) => {
      setIsRotating(true);
      lastMousePos.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const handleMouseMove = (e) => {
      if (!isRotating || !modelRef.current) return;

      const deltaX = e.clientX - lastMousePos.current.x;
      const deltaY = e.clientY - lastMousePos.current.y;

      modelRef.current.rotation.y += deltaX * 0.01;
      modelRef.current.rotation.x += deltaY * 0.01;

      lastMousePos.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const handleMouseUp = () => {
      setIsRotating(false);
    };

    // Add event listeners
    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // ✅ Load STEP File using JSCAD
    const loadStepFile = async () => {
      try {
        setLoading(true);
        console.log('Fetching STEP file:', url);

        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const stepData = await response.arrayBuffer(); // STEP files are binary

        console.log('STEP file loaded!');
        const { step } = deserializers;
        const { geometries } = step({ input: stepData });
        

        // ✅ Parse STEP file using JSCAD
        // const { geometries } = io.deserialize({ input: stepData, output: 'geometry' });

        if (!geometries || geometries.length === 0) throw new Error('Failed to parse STEP file');

        // ✅ Convert JSCAD Geometry to Three.js Mesh
        const geometry = new THREE.BufferGeometry();
        const vertices = [];

        geometries.forEach((geom) => {
          if (geom.polygons) {
            geom.polygons.forEach((polygon) => {
              polygon.vertices.forEach((vertex) => {
                vertices.push(vertex[0], vertex[1], vertex[2]);
              });
            });
          }
        });

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.computeVertexNormals();

        const material = new THREE.MeshPhongMaterial({ color: 0x999999, side: THREE.DoubleSide });
        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);
        modelRef.current = mesh;

        setLoading(false);
      } catch (err) {
        console.error('Error loading STEP file:', err);
        setError('Failed to load model');
        setLoading(false);
      }
    };

    loadStepFile();


    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;

      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    window.addEventListener('resize', handleResize);

    // Load model (basic cube for demo)
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshPhongMaterial({
      color: 0x999999,
      transparent: true,
      opacity: 1 - transparency / 100
    });
    const cube = new THREE.Mesh(geometry, material);
    modelRef.current = cube;
    scene.add(cube);
    setLoading(false);

    return () => {
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleResize);
      scene.clear();
      renderer.dispose();
      containerRef.current?.removeChild(renderer.domElement);
    };
  }, [transparency, isRotating]);

  const handleZoomIn = () => {
    if (cameraRef.current) {
      cameraRef.current.position.z *= 0.8;
    }
  };

  const handleZoomOut = () => {
    if (cameraRef.current) {
      cameraRef.current.position.z *= 1.2;
    }
  };

  const handleReset = () => {
    if (modelRef.current) {
      modelRef.current.rotation.set(0, 0, 0);
      if (cameraRef.current) {
        cameraRef.current.position.set(0, 0, 5);
      }
    }
  };

  const handleViewMode = (mode) => {
    setViewMode(mode);
    if (modelRef.current) {
      modelRef.current.material.wireframe = mode === 'wireframe';
    }
  };

  return (
    <div className="w-full h-full flex flex-col rounded-lg overflow-hidden border bg-white">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex gap-2">
          <button
            onClick={() => handleViewMode('shaded')}
            className={`px-3 py-1 rounded ${
              viewMode === 'shaded' ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Shaded
          </button>
          <button
            onClick={() => handleViewMode('wireframe')}
            className={`px-3 py-1 rounded ${
              viewMode === 'wireframe' ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Wireframe
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleZoomIn}
            className="p-2 hover:bg-gray-200 rounded"
            title="Zoom In"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 hover:bg-gray-200 rounded"
            title="Zoom Out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <button
            onClick={handleReset}
            className="p-2 hover:bg-gray-200 rounded"
            title="Reset View"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              const renderer = rendererRef.current;
              if (renderer) {
                const screenshot = renderer.domElement.toDataURL('image/png');
                const link = document.createElement('a');
                link.download = 'model-screenshot.png';
                link.href = screenshot;
                link.click();
              }
            }}
            className="p-2 hover:bg-gray-200 rounded"
            title="Screenshot"
          >
            <Camera className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Transparency control */}
      <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-2">
        <Eye className="w-4 h-4" />
        <input
          type="range"
          min="0"
          max="100"
          value={transparency}
          onChange={(e) => setTransparency(parseInt(e.target.value))}
          className="w-32"
        />
        <span className="text-sm">{transparency}%</span>
      </div>

      {/* Viewer container */}
      <div 
        ref={containerRef} 
        className="flex-1 relative"
        style={{ cursor: isRotating ? 'grabbing' : 'grab' }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <div className="text-lg">Loading model...</div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-50 bg-opacity-75">
            <div className="text-red-600 p-4 rounded bg-white shadow">
              <h3 className="font-bold mb-2">Error Loading Model</h3>
              <p>{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelViewer;